import { toPlatform, type SoundPlatform } from './platform';

/**
 * Finding sounds in the places the server mentions them: stored search
 * params, a lead's `discoverySource`, a schedule's settings summary, and the
 * free text of job labels and log lines. Pure — the components in
 * components/SoundLink.tsx render what these find.
 */

export interface SoundRef {
  platform: SoundPlatform;
  /** Kept as a string: TikTok ids exceed JS safe integers. */
  id: string;
}

export const soundKey = (r: SoundRef): string => `${r.platform}:${r.id}`;

const TIKTOK_URL_ID = /tiktok\.com\/music\/[^?#\s]*?-(\d{6,})/i;
const SHARE_PARAM = /[?&]share_music_id=(\d{6,})/i;
const IG_URL_ID = /audio\/(\d{5,})/i;
const BARE_ID = /^\d{5,}$/;

/** The id in a bare id or a sound page URL; null when there is none. */
export function parseSoundId(platform: SoundPlatform, raw: unknown): string | null {
  const s = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
  if (!s) return null;
  if (BARE_ID.test(s)) return s;
  const m = platform === 'instagram' ? IG_URL_ID.exec(s) : TIKTOK_URL_ID.exec(s) ?? SHARE_PARAM.exec(s);
  return m?.[1] ?? null;
}

export function uniqueSoundRefs(refs: readonly SoundRef[]): SoundRef[] {
  const seen = new Set<string>();
  return refs.filter((r) => {
    const k = soundKey(r);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** The sounds a stored search ran on (`musicIds`, or the older `musicId`). Empty for other modes. */
export function soundRefsFromParams(raw: Record<string, unknown> | null | undefined): SoundRef[] {
  const p = raw ?? {};
  if (p.searchMode !== 'sound') return [];
  const platform: SoundPlatform = toPlatform(p.platform) === 'instagram' ? 'instagram' : 'tiktok';
  const values = [...list(p.musicIds), ...(p.musicId != null && p.musicId !== '' ? [p.musicId] : [])];
  return uniqueSoundRefs(
    values.map((v) => parseSoundId(platform, v)).filter((id): id is string => !!id).map((id) => ({ platform, id })),
  );
}

/** A lead's `discoverySource`: `sound:<id>` (TikTok) or `audio:<id>` (Instagram). */
export function soundRefFromSource(source: string | null | undefined): SoundRef | null {
  const m = /^(sound|audio):(\d{5,})$/.exec((source ?? '').trim());
  if (!m) return null;
  return { platform: m[1] === 'audio' ? 'instagram' : 'tiktok', id: m[2]! };
}

/**
 * The sounds in one setting of a schedule's server-built summary, or null
 * when the setting isn't about sounds. The server labels the query entry
 * with the mode's own label ("TikTok sounds", "Instagram audio") and lists
 * the ids comma-separated; stray `musicIds` / `musicId` keys are sounds too.
 */
export function soundRefsFromSetting(key: string, label: string, value: string | null | undefined): SoundRef[] | null {
  const isQuery = key === 'query' && /\b(sounds?|audio)\b/i.test(label);
  if (!isQuery && key !== 'musicIds' && key !== 'musicId') return null;
  const platform: SoundPlatform = /instagram|audio/i.test(label) ? 'instagram' : 'tiktok';
  const refs = (value ?? '')
    .split(/,\s*/)
    .map((v) => parseSoundId(platform, v))
    .filter((id): id is string => !!id)
    .map((id) => ({ platform, id }));
  return refs.length > 0 ? uniqueSoundRefs(refs) : null;
}

export type TextPart = { text: string } | { sound: SoundRef; text: string };

/**
 * Split server text on the sounds it names, so they can be rendered as
 * links: "sound 7656…" / "sound:7656…" (TikTok, or Instagram on a line that
 * starts "Instagram ·") and "audio 2402…" / "audio:2402…" (Instagram), with
 * the id bare or as a page URL.
 */
export function splitSoundMentions(text: string): TextPart[] {
  const parts: TextPart[] = [];
  const instagramLine = /^\s*Instagram\b/i.test(text);
  const re = /\b(sound|audio)([ :])(https?:\/\/\S+|\d{5,25})\b\/?/gi;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const word = m[1]!.toLowerCase();
    const token = m[3]!;
    const platform: SoundPlatform =
      word === 'audio' || /instagram\.com/i.test(token) || (instagramLine && !/tiktok\.com/i.test(token)) ? 'instagram' : 'tiktok';
    const id = parseSoundId(platform, token);
    if (!id) continue;
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ sound: { platform, id }, text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
